import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { ReactReduxContext } from './Context';
import { createSubscription } from '../utils/Subscription';
import { getUseIsomorphicLayoutEffect } from '../utils/getUseIsomorphicLayoutEffect';

function Provider(_ref) {
  var store = _ref.store,
      context = _ref.context,
      children = _ref.children,
      ssr = _ref.ssr;
  var contextValue = useMemo(function () {
    var subscription = createSubscription(store);
    subscription.onStateChange = subscription.notifyNestedSubs;
    return {
      ssr: ssr,
      store: store,
      subscription: subscription
    };
  }, [store]); // NOTE: we intentionally exclude `ssr` since we shouldn't be changing its value

  var previousState = useMemo(function () {
    return store.getState();
  }, [store]); // NOTE(kevinb): Although getUseIsomorphicLayoutEffect() can return a different hook
  // depending on the value of contextValue.ssr and thus break the rules of hooks, this
  // will never happen because the value of contextValue.ssr can never change.

  var useIsomorphicLayoutEffect = getUseIsomorphicLayoutEffect(contextValue.ssr);
  useIsomorphicLayoutEffect(function () {
    var subscription = contextValue.subscription;
    subscription.trySubscribe();

    if (previousState !== store.getState()) {
      subscription.notifyNestedSubs();
    }

    return function () {
      subscription.tryUnsubscribe();
      subscription.onStateChange = null;
    };
  }, [contextValue, previousState]);
  var Context = context || ReactReduxContext;
  return /*#__PURE__*/React.createElement(Context.Provider, {
    value: contextValue
  }, children);
}

if (process.env.NODE_ENV !== 'production') {
  Provider.propTypes = {
    store: PropTypes.shape({
      subscribe: PropTypes.func.isRequired,
      dispatch: PropTypes.func.isRequired,
      getState: PropTypes.func.isRequired
    }),
    context: PropTypes.object,
    children: PropTypes.any,
    ssr: PropTypes.bool
  };
}

export default Provider;